import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'wouter';
import { BackIcon } from '../../components/chrome/BackIcon.js';
import { PageActions } from '../../components/chrome/icons.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import {
  getAppointments,
  getReminders,
  restoreAppointment,
  restoreReminder,
} from '../../lib/api/personal.js';
import {
  addDays,
  countByDay,
  dateLabel,
  dateOfKey,
  dayKey,
  dayTitle,
  entriesOf,
  entriesOn,
  freeLabel,
  freeWindows,
  holidayName,
  monthGrid,
  repeatLabel,
  weekOf,
  whenLabel,
  type AgendaEntry,
} from '../../lib/agendaView.js';
import { hapticTap } from '../../lib/native.js';
import { refreshStageData } from '../../lib/stage/refresh.js';
import { EventSheet, type Removed, type SheetTarget } from './EventSheet.js';
import { AGENDA_TRASH_KEY, TrashSheet } from './TrashSheet.js';
import './agenda.css';

type View = 'day' | 'week' | 'month';
const VIEWS: { id: View; label: string }[] = [
  { id: 'day', label: 'Dia' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
];
const WEEKDAY_LETTERS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const VIEW_KEY = 'secretario.agenda.visao';
const UNDO_MS = 6000;

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12M10.5 11v5M13.5 11v5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const RoutineIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M12 7.5V12l3 2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const Chevron = ({ back = false }: { back?: boolean }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path
      d={back ? 'M14.5 6l-6 6 6 6' : 'M9.5 6l6 6-6 6'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function readView(): View {
  try {
    const saved = window.localStorage.getItem(VIEW_KEY);
    return saved === 'week' || saved === 'month' ? saved : 'day';
  } catch {
    return 'day';
  }
}

/**
 * Agenda: o dia, a semana e o mês, com tudo o que a pessoa e o Secretário
 * marcaram. Criar e editar acontecem num painel; o que é apagado vai para a
 * lixeira por 30 dias e pode ser desfeito na hora.
 */
export function AgendaPage() {
  const queryClient = useQueryClient();
  const today = dayKey(new Date());
  const [view, setViewState] = useState<View>(readView);
  const [selected, setSelected] = useState(today);
  // Na semana: um toque no dia mostra só aquele dia; outro toque no mesmo dia volta à semana.
  const [dayOnly, setDayOnly] = useState(false);
  const [sheet, setSheet] = useState<SheetTarget | null>(null);
  const [trashOpen, setTrashOpen] = useState(false);

  // Vindo do painel do palco: "Lembrete" e "Compromisso" já abrem o painel de criar;
  // "Ver semana" abre na semana, sem trocar a visão que a pessoa deixou guardada.
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const wanted = query.get('novo');
    if (query.get('ver') === 'semana') setViewState('week');
    // O Assistir abre no dia do que foi criado (?dia=AAAA-MM-DD), para o item acender na tela.
    const wantedDay = query.get('dia');
    if (wantedDay && /^\d{4}-\d{2}-\d{2}$/.test(wantedDay)) {
      setSelected(wantedDay);
      setViewState('day');
    }
    if (wanted === 'lembrete' || wanted === 'compromisso') {
      setSheet({
        mode: 'new',
        date: dayKey(new Date()),
        kind: wanted === 'lembrete' ? 'reminder' : 'appointment',
      });
    }
    if (wanted || query.has('ver') || query.has('dia')) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);
  const [toast, setToast] = useState<{ text: string; removed?: Removed } | null>(null);

  const setView = (next: View) => {
    setViewState(next);
    setDayOnly(false);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Sem armazenamento, a visão só não é lembrada.
    }
  };

  // Busca o mês em grade do dia escolhido (cobre também o dia e a semana).
  const anchor = dateOfKey(selected);
  const grid = useMemo(
    () => monthGrid(anchor.getFullYear(), anchor.getMonth()),
    [anchor.getFullYear(), anchor.getMonth()],
  );
  const week = weekOf(selected);
  const rangeStart = [grid[0]![0]!.key, week[0]!].sort()[0]!;
  const rangeEnd = addDays([grid.at(-1)!.at(-1)!.key, week[6]!].sort()[1]!, 1);
  const agendaQ = useQuery({
    queryKey: ['agenda', rangeStart, rangeEnd],
    queryFn: async () => {
      const [appointments, reminders] = await Promise.all([
        getAppointments(dateOfKey(rangeStart).toISOString(), dateOfKey(rangeEnd).toISOString()),
        getReminders(),
      ]);
      return entriesOf(appointments, reminders);
    },
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
  const entries = useMemo(() => agendaQ.data ?? [], [agendaQ.data]);
  const counts = useMemo(() => countByDay(entries), [entries]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.removed ? UNDO_MS : 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['agenda'] });
    void queryClient.invalidateQueries({ queryKey: AGENDA_TRASH_KEY });
    refreshStageData(queryClient);
  };

  async function undo(removed: Removed) {
    setToast(null);
    try {
      if (removed.kind === 'reminder') await restoreReminder(removed.id);
      else await restoreAppointment(removed.id, removed.occurrenceStart);
      refresh();
      setToast({ text: 'Pronto, voltou para a agenda.' });
    } catch {
      setToast({ text: 'Não consegui desfazer agora. Ele está na lixeira.' });
    }
  }

  const move = (step: number) => {
    hapticTap();
    if (view === 'day') setSelected(addDays(selected, step));
    else if (view === 'week') setSelected(addDays(selected, step * 7));
    else {
      const next = new Date(anchor.getFullYear(), anchor.getMonth() + step, 1);
      setSelected(dayKey(next));
    }
  };

  const heading =
    view === 'day'
      ? dayTitle(selected)
      : view === 'week'
        ? `${dateLabel(week[0]!).split(', ')[1]} a ${dateLabel(week[6]!).split(', ')[1]}`
        : `${MONTHS[anchor.getMonth()]} de ${anchor.getFullYear()}`;
  const stepName = view === 'day' ? 'dia' : view === 'week' ? 'semana' : 'mês';
  const loading = agendaQ.isLoading;

  return (
    <div className="agv">
      <div className="agv-wrap">
        <header className="agv-hd">
          <Link href="/" className="back-btn" aria-label="Voltar para a tela inicial">
            <BackIcon />
          </Link>
          <PageActions>
            <Link
              href="/agenda/rotina"
              className="icon-btn"
              aria-label="Sua rotina e horários livres"
            >
              <RoutineIcon />
            </Link>
            <button className="icon-btn" aria-label="Lixeira" onClick={() => setTrashOpen(true)}>
              <TrashIcon />
            </button>
          </PageActions>
        </header>

        <div className="agv-kicker">Sua agenda</div>
        <div className="agv-title">
          <button className="agv-step" onClick={() => move(-1)} aria-label={`${stepName} anterior`}>
            <Chevron back />
          </button>
          <h1 aria-live="polite">{heading}</h1>
          <button className="agv-step" onClick={() => move(1)} aria-label={`Próximo ${stepName}`}>
            <Chevron />
          </button>
        </div>

        <div className="agv-bar">
          <div className="stg-seg agv-views" role="tablist" aria-label="Como ver a agenda">
            {VIEWS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={view === item.id}
                className={view === item.id ? 'on' : ''}
                onClick={() => setView(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button
            className="agv-today"
            onClick={() => {
              hapticTap();
              setSelected(today);
            }}
            disabled={selected === today}
          >
            Hoje
          </button>
        </div>

        {agendaQ.isError && (
          <div className="agv-card agv-empty" role="alert">
            <p>Não consegui abrir a agenda agora.</p>
            <button className="stg-btn" onClick={() => void agendaQ.refetch()}>
              Tentar de novo
            </button>
          </div>
        )}

        {view === 'month' && (
          <section className="agv-card agv-month" aria-label={heading}>
            <div className="agv-wd" aria-hidden="true">
              {WEEKDAY_LETTERS.map((letter, index) => (
                <span key={WEEKDAY_NAMES[index]}>{letter}</span>
              ))}
            </div>
            {grid.map((cells) => (
              <div key={cells[0]!.key} className="agv-row">
                {cells.map((cell) => {
                  const count = counts.get(cell.key) ?? 0;
                  const holiday = holidayName(cell.key);
                  return (
                    <button
                      key={cell.key}
                      className={`agv-cell${cell.inMonth ? '' : ' out'}${cell.key === today ? ' today' : ''}${cell.key === selected ? ' sel' : ''}${holiday ? ' hol' : ''}`}
                      onClick={() => {
                        hapticTap();
                        setSelected(cell.key);
                      }}
                      aria-label={`${dateLabel(cell.key)}${holiday ? `, ${holiday}` : ''}${count ? `, ${count} ${count === 1 ? 'coisa' : 'coisas'}` : ''}`}
                      aria-pressed={cell.key === selected}
                    >
                      <b>{cell.day}</b>
                      <span className="agv-dots" aria-hidden="true">
                        {count > 3 ? (
                          <em>{count}</em>
                        ) : (
                          Array.from({ length: count }, (_, index) => <i key={index} />)
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            <p className="agv-legend">
              <span>
                <i /> coisa marcada
              </span>
              <span className="hol">
                <b>12</b> feriado
              </span>
            </p>
          </section>
        )}

        {view === 'week' && (
          <div className="agv-strip" role="group" aria-label="Dias da semana">
            {week.map((key) => {
              const date = dateOfKey(key);
              const count = counts.get(key) ?? 0;
              return (
                <button
                  key={key}
                  className={`agv-pill${key === today ? ' today' : ''}${key === selected ? ' sel' : ''}${holidayName(key) ? ' hol' : ''}`}
                  onClick={() => {
                    hapticTap();
                    setDayOnly(!(dayOnly && key === selected));
                    setSelected(key);
                  }}
                  aria-label={`${dateLabel(key)}${count ? `, ${count} ${count === 1 ? 'coisa' : 'coisas'}` : ''}`}
                  aria-pressed={dayOnly && key === selected}
                >
                  <small>{WEEKDAY_LETTERS[date.getDay()]}</small>
                  <b>{date.getDate()}</b>
                  <span className="agv-dots" aria-hidden="true">
                    {count > 0 && <i />}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="agv-card">
            <Skeleton variant="calendar" label="Abrindo sua agenda" />
          </div>
        ) : view === 'week' && !dayOnly ? (
          <WeekList
            week={week}
            entries={entries}
            today={today}
            onOpen={(entry) => setSheet({ mode: 'edit', entry })}
            onPick={setSelected}
          />
        ) : (
          <DayList
            day={selected}
            entries={entries}
            showTitle={view === 'month' || dayOnly}
            onOpen={(entry) => setSheet({ mode: 'edit', entry })}
          />
        )}

        <EventSheet
          target={sheet}
          onClose={() => setSheet(null)}
          onSaved={(message) => {
            setSheet(null);
            refresh();
            setToast({ text: message });
          }}
          onRemoved={(removed) => {
            setSheet(null);
            refresh();
            setToast({ text: `Apagado: ${removed.label}.`, removed });
          }}
        />
        <TrashSheet
          open={trashOpen}
          onClose={() => setTrashOpen(false)}
          onRestored={(message) => {
            refresh();
            setToast({ text: message });
          }}
        />

        {/* Fora da página: ela entra com animação, e o que é fixo precisa ficar preso à tela. */}
        {createPortal(
          <>
            <div className="agv-new">
              <button
                className="agv-newbtn"
                onClick={() => {
                  hapticTap();
                  setSheet({ mode: 'new', date: selected, kind: 'appointment' });
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                  />
                </svg>
                Novo
              </button>
            </div>
            {toast && (
              <div className="agv-toast" role="status">
                <span>{toast.text}</span>
                {toast.removed && (
                  <button onClick={() => void undo(toast.removed!)}>Desfazer</button>
                )}
              </div>
            )}
          </>,
          document.body,
        )}
      </div>
    </div>
  );
}

function EntryRow({ entry, onOpen }: { entry: AgendaEntry; onOpen: (entry: AgendaEntry) => void }) {
  const place = entry.appointment?.location?.trim();
  const sub = [
    entry.kind === 'reminder' ? 'Lembrete' : 'Compromisso',
    entry.appointment?.contactName?.trim() ? `com ${entry.appointment.contactName.trim()}` : '',
    place ?? '',
    entry.repeats ? repeatLabel(entry.appointment?.rrule) || 'Repete' : '',
  ].filter(Boolean);
  const [hour, ...rest] = whenLabel(entry).split(' às ');
  return (
    <button
      className={`stg-row link agv-entry ${entry.kind}`}
      // O id do compromisso ou do lembrete: o modo Assistir acende o que acabou de ser criado.
      data-sec-item={entry.key.slice(2)}
      onClick={() => onOpen(entry)}
    >
      <span className={`stg-chip${entry.allDay ? ' wide' : ''}`}>
        {entry.allDay ? 'Dia todo' : hour}
        {rest.length > 0 && <small>até {rest[0]}</small>}
      </span>
      <span className="stg-row-t">
        <b>{entry.title}</b>
        <small>{sub.join(' · ')}</small>
      </span>
      <span className="stg-go" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

function DayList({
  day,
  entries,
  showTitle,
  onOpen,
}: {
  day: string;
  entries: AgendaEntry[];
  showTitle: boolean;
  onOpen: (entry: AgendaEntry) => void;
}) {
  const items = entriesOn(entries, day);
  const holiday = holidayName(day);
  const free = day >= dayKey(new Date()) ? freeLabel(freeWindows(entries, day)) : '';
  const busy = items.some((entry) => entry.kind === 'appointment' && !entry.allDay);
  return (
    <section className="agv-day" aria-label={dayTitle(day)}>
      {showTitle && <h2 className="stg-sub">{dayTitle(day)}</h2>}
      {holiday && <p className="agv-holiday">Feriado: {holiday}</p>}
      {items.length === 0 ? (
        <p className="agv-card agv-empty">
          Nada marcado {day === dayKey(new Date()) ? 'para hoje' : 'neste dia'}. Toque em Novo ou me
          peça por voz.
        </p>
      ) : (
        <div className="sk-cascade stg-list">
          {items.map((entry) => (
            <EntryRow key={`${entry.key}-${day}`} entry={entry} onOpen={onOpen} />
          ))}
        </div>
      )}
      {busy && free && <p className="agv-free">{free}</p>}
    </section>
  );
}

function WeekList({
  week,
  entries,
  today,
  onOpen,
  onPick,
}: {
  week: string[];
  entries: AgendaEntry[];
  today: string;
  onOpen: (entry: AgendaEntry) => void;
  onPick: (key: string) => void;
}) {
  const days = week
    .map((key) => ({ key, items: entriesOn(entries, key), holiday: holidayName(key) }))
    .filter((day) => day.items.length > 0 || day.holiday);
  if (days.length === 0) {
    return <p className="agv-card agv-empty">Semana livre. Toque em Novo ou me peça por voz.</p>;
  }
  return (
    <div className="agv-week">
      {days.map((day) => (
        <section key={day.key} className="agv-day">
          <button
            className={`agv-dayhd${day.key === today ? ' today' : ''}`}
            onClick={() => onPick(day.key)}
          >
            <span>{dayTitle(day.key)}</span>
            {day.holiday && <em>{day.holiday}</em>}
          </button>
          <div className="stg-list">
            {day.items.map((entry) => (
              <EntryRow key={`${entry.key}-${day.key}`} entry={entry} onOpen={onOpen} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
